import React, { useState, useEffect } from "react";
import Modal from "react-modal";
import { X } from "lucide-react";
import { useAuth } from "../../src/context/AuthContext";
import { useTranslation } from "react-i18next";
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  updateDoc,
  getDoc,
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import { ClipLoader } from "react-spinners";
import { toast } from "react-toastify";
import { fetchPlansFromFirebase } from "../utils/fetchPlansFromFirebase";

Modal.setAppElement("#root");

const customStyles = {
  overlay: {
    backgroundColor: "rgba(0, 0, 0, 0.75)",
    zIndex: 1000,
  },
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    transform: "translate(-50%, -50%)",
    maxWidth: "850px",
    maxHeight: "95vh",
    overflowY: "auto",
    width: "100%",
    padding: "20px",
    borderRadius: "24px",
    backgroundColor: "white",
    border: "none",
  },
};

const PlansModal = ({ isOpen, onClose, initialTab = "subscriptions", onTrialActivated, directFreeTrial = false }) => {
  const { user, setUser } = useAuth();
  const [activeTab, setActiveTab] = useState(initialTab);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();
  const [isLoadingPlans, setIsLoadingPlans] = useState(true);
  const [error, setError] = useState(null);
  const [offerPlans, setOfferPlans] = useState([]);
  const [showOffers, setShowOffers] = useState(false);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [creditPlans, setCreditPlans] = useState([]);
  const [showFreeTrialModal, setShowFreeTrialModal] = useState(false);
  const [freeTrialActivated, setFreeTrialActivated] = useState(false);
  const [hasActiveFreeTrial, setHasActiveFreeTrial] = useState(false);

  // Check if user already has free access
  useEffect(() => {
    if (user?.uid) {
      const checkFreeAccess = async () => {
        try {
          const userRef = doc(db, "students", user.uid);
          const userDoc = await getDoc(userRef);
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setHasActiveFreeTrial(userData.freeAccess === true);
          }
        } catch (error) {
          console.error("Error checking free access:", error);
        }
      };
      checkFreeAccess();
    }
  }, [user?.uid]);

  // Reset active tab when initialTab changes
  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  // Automatically open Free Trial modal when directFreeTrial is true
  useEffect(() => {
    if (directFreeTrial && isOpen && !isLoadingPlans && !hasActiveFreeTrial) {
      setShowFreeTrialModal(true);
    }
  }, [directFreeTrial, isOpen, isLoadingPlans, hasActiveFreeTrial]);

  const checkNewUserEligibility = async (userId) => {
    try {
      const userDoc = await getDocs(
        query(
          collection(db, "user_accounts"),
          where("email", "==", user.email),
        ),
      );

      if (!userDoc.empty) {
        const userData = userDoc.docs[0].data();
        const createdAt = userData.created_at?.toDate() || new Date();
        const daysSinceCreation =
          (new Date() - createdAt) / (1000 * 60 * 60 * 24);
        return daysSinceCreation <= 7;
      }
      return false;
    } catch (error) {
      console.error("Error checking user eligibility:", error);
      return false;
    }
  };

  // Fetch plans and check user's subscription status
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setIsLoadingPlans(true);
        setError(null);

        // Fetch all plans from Firebase
        const { subscriptionPlans, creditPlans, offersData } =
          await fetchPlansFromFirebase(t);

        // Check if user is eligible for offers
        const isEligible = await checkNewUserEligibility(user?.uid);
        setShowOffers(isEligible);

        // Handle offer plans
        if (isEligible) {
          const transformedOfferPlans = [];

          if (!hasActiveFreeTrial) {
            transformedOfferPlans.push({
              id: "free-trial",
              title: t("plans-modal.subscription-plans.group.title"),
              description: t("plans-modal.offer-plans.free.description"),
              price: 0,
              isPopular: false,
              type: "free_trial",
              body: t("plans-modal.offer-plans.free.body"),
            });
          }

          setOfferPlans(transformedOfferPlans);
        }

        // Set subscription and credit plans
        setSubscriptionPlans(subscriptionPlans);
        setCreditPlans(creditPlans);
      } catch (err) {
        console.error("Error fetching plans:", err);
        setError("Failed to load plans. Please try again later.");
      } finally {
        setIsLoadingPlans(false);
      }
    };

    fetchPlans();
  }, [t, user, hasActiveFreeTrial]);

  const handlePurchase = async (plan, userId) => {
    try {
      // Get the base URL without any query parameters
      const baseUrl = plan.stripeLink.split("?")[0];

      // Add metadata to the URL
      const url = new URL(baseUrl);
      url.searchParams.set("client_reference_id", userId);
      url.searchParams.set("prefilled_email", user?.email || "");
      url.searchParams.set("metadata[studentId]", userId);
      url.searchParams.set("metadata[paymentType]", plan.type);

      // Redirect to Stripe with metadata
      window.location.href = url.toString();
    } catch (error) {
      console.error("Error redirecting to checkout:", error);
      // Handle error (show error message to user)
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSelectedPlan(null);
  };

  const handleSubscribe = async () => {
    if (!selectedPlan) return;

    setIsLoading(true);
    try {
      // Handle free access plan differently
      if (selectedPlan.type === "free_trial") {
        // Check if user already has active free trial
        if (hasActiveFreeTrial) {
          toast.warning("You already have an active free subscription");
          return;
        }
        
        setShowFreeTrialModal(true);
        return;
      }

      // For all other plans, redirect to Stripe
      await handlePurchase(selectedPlan, user?.uid);
    } finally {
      setIsLoading(false);
    }
  };

  // Activate free trial
  const activateFreeTrial = async () => {
    setIsLoading(true);
    try {
      const userRef = doc(db, "students", user.uid);
      await updateDoc(userRef, {
        freeAccess: true,
        subscriptions: [
          {
            endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
            startDate: new Date(),
            type: "Free Trial",
          },
        ],
      });

      // Update user context and session storage
      const updatedUser = {
        ...user,
        freeAccess: true,
        subscriptions: [
          {
            endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
            startDate: new Date(),
            type: "Free Trial",
          },
        ],
      };

      // Update context
      setUser(updatedUser);

      // Update session storage
      const storedUser = JSON.parse(sessionStorage.getItem("user") || "{}");
      const updatedStoredUser = {
        ...storedUser,
        freeAccess: true,
        subscriptions: [
          {
            endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
            startDate: new Date(),
            type: "Free Trial",
          },
        ],
      };
      sessionStorage.setItem("user", JSON.stringify(updatedStoredUser));

      setHasActiveFreeTrial(true); // Update local state
      setFreeTrialActivated(true); // Show success state
      
      // Small delay to show success message
      await new Promise(resolve => setTimeout(resolve, 600));
      
      // Close the modal and start auto-join seamlessly
      setShowFreeTrialModal(false);
      setFreeTrialActivated(false); // Reset activation state
      setSelectedPlan(null); // Reset selected plan
      onClose();
      
      // Keep loading state active if there's an auto-join callback
      if (onTrialActivated) {
        // Don't set loading to false yet - let the parent handle loading state
        await onTrialActivated();
      }
    } catch (error) {
      console.error("Error activating free trial:", error);
      toast.error("Failed to activate free trial");
      setIsLoading(false);
    }
  };

  const SubscriptionPlan = ({ plan, userId, isSelected, onSelect }) => {
    const isFree =
      plan.price === 0 || plan.title.toLowerCase().includes("free");
    const features = [
      {
        title: t("plans-modal.features.classes.group.title"),
        description: t("plans-modal.features.classes.group.description"),
      },
      {
        title: t("plans-modal.features.experts.title"),
        description: t("plans-modal.features.experts.description"),
      },
      {
        title: t("plans-modal.features.resources.title"),
        description: t("plans-modal.features.resources.description"),
      },
      {
        title: t("plans-modal.features.supertutor.title"),
        description: t("plans-modal.features.supertutor.description"),
      },
    ];

    return (
      <div
        onClick={() => onSelect(plan)}
        className={`flex h-full cursor-pointer flex-col rounded-3xl transition-all duration-200 ${
          isSelected
            ? "scale-[1.02] transform border-2 border-[#14B82C] bg-[#E6FDE9] shadow-lg"
            : "border border-[#B0B0B0] bg-white hover:border-[#14B82C] hover:shadow-md"
        } ${isFree && hasActiveFreeTrial ? "relative" : ""}`}
      >
        {plan.isPopular ? (
          <div className="rounded-t-3xl bg-[#FFBF00] px-4 py-1.5 text-center text-sm font-semibold">
            {t("plans-modal.popular-badge")}
          </div>
        ) : (
          <div className="h-[30px]" />
        )}
        <div className="flex flex-grow flex-col space-y-4 p-5 text-center">
          <div className="space-y-4">
            <div className="text-4xl font-semibold">
              {plan.price === 0 ? "FREE" : `$${plan.price}`}
              {plan.price !== 0 && (
                <span className="text-base font-normal text-black">
                  /{plan.period}
                </span>
              )}
            </div>
            <div>
              <h3 className="text-md mb-2 font-medium">{plan.title}</h3>
              <p className="text-sm text-black">{plan.description}</p>
            </div>
          </div>

          <div className="flex-grow space-y-3">
            {features.map((feature, index) => (
              <div
                key={index}
                className="flex items-center justify-center gap-3 text-center"
              >
                <div className="flex flex-col items-center justify-center">
                  <img
                    alt="crown"
                    src="/svgs/crown-new.svg"
                    className="h-4 w-4"
                  />
                  <div>
                    <div className="font-sm">{feature.title}</div>
                    <div className="text-xs text-black">
                      {feature.description}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const OfferPlan = ({ plan, userId, isSelected, onSelect }) => {
    return (
      <div
        onClick={() => onSelect(plan)}
        className={`flex h-full cursor-pointer flex-col rounded-3xl transition-all duration-200 ${
          isSelected
            ? "scale-[1.02] transform border-2 border-[#14B82C] bg-[#E6FDE9] shadow-lg"
            : "border border-[#B0B0B0] bg-white hover:border-[#14B82C] hover:shadow-md"
        }`}
      >
        {plan.isPopular ? (
          <div className="rounded-t-3xl bg-[#FFBF00] px-4 py-1.5 text-center text-sm font-semibold">
            {t("plans-modal.popular-badge")}
          </div>
        ) : (
          <div className="h-[30px]" />
        )}
        <div className="flex flex-grow flex-col space-y-6 p-6 text-center">
          <div className="space-y-4">
            <div className="text-4xl font-bold">
              {plan.price === 0 ? "FREE" : `$${plan.price}`}
            </div>
            <div className="space-y-2">
              <h3 className="text-lg font-medium">{plan.title}</h3>
              {plan.description && (
                <p className="text-sm text-black">{plan.description}</p>
              )}
            </div>
            {plan.body && <p className="text-sm text-black">{plan.body}</p>}
          </div>
          <div className="flex-grow" />
        </div>
      </div>
    );
  };

  const handlePlanSelect = (plan) => {
    setSelectedPlan(selectedPlan?.id === plan.id ? null : plan);
  };

  if (isLoadingPlans) {
    return (
      <Modal
        isOpen={isOpen}
        onRequestClose={onClose}
        style={customStyles}
        contentLabel="Membership Modal"
      >
        <div className="flex h-64 items-center justify-center">
          <ClipLoader color="#14B82C" size={50} />
        </div>
      </Modal>
    );
  }

  if (error) {
    return (
      <Modal
        isOpen={isOpen}
        onRequestClose={onClose}
        style={customStyles}
        contentLabel="Membership Modal"
      >
        <div className="text-center">
          <p className="mb-4 text-red-500">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="rounded-full bg-[#14B82C] px-4 py-2 text-[#042F0C]"
          >
            Try Again
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <>
    {/* Main Offers modal */}
      <Modal
        isOpen={isOpen && !showFreeTrialModal}
        onRequestClose={onClose}
        style={customStyles}
        contentLabel="Membership Modal"
      >
        <div className="relative font-urbanist">
          <div className="mb-6 flex items-center justify-between">
            <h2 className="text-2xl font-medium">{t("plans-modal.title")}</h2>
            <button
              onClick={onClose}
              className="p-1 text-gray-400 hover:text-gray-600"
            >
              <X size={20} />
            </button>
          </div>

          <div className="mb-6 flex justify-center">
            <div className="inline-flex rounded-full border border-gray-300 bg-gray-100">
              {showOffers && (
                <button
                  className={`rounded-full px-6 py-2 text-sm font-medium text-[#042F0C] transition-colors ${
                    activeTab === "offers"
                      ? "border border-[#042F0C] bg-[#FFBF00]"
                      : "bg-transparent"
                  }`}
                  onClick={() => handleTabChange("offers")}
                >
                  {t("plans-modal.tabs.offers")}
                </button>
              )}
              <button
                className={`rounded-full px-6 py-2 text-sm font-medium text-[#042F0C] transition-colors ${
                  activeTab === "subscriptions"
                    ? "border border-[#042F0C] bg-[#FFBF00]"
                    : "bg-transparent"
                }`}
                onClick={() => handleTabChange("subscriptions")}
              >
                {t("plans-modal.tabs.subscriptions")}
              </button>
              <button
                className={`rounded-full px-6 py-2 text-sm font-medium text-[#042F0C] transition-colors ${
                  activeTab === "exam"
                    ? "border border-[#042F0C] bg-[#FFBF00]"
                    : "bg-transparent"
                }`}
                onClick={() => handleTabChange("exam")}
              >
                {t("plans-modal.tabs.exam-prep")}
              </button>
            </div>
          </div>

          {activeTab === "subscriptions" && (
            <div className="flex justify-center">
              <div className="w-full max-w-md">
                {subscriptionPlans.slice(0, 1).map((plan, index) => (
                  <SubscriptionPlan
                    key={index}
                    plan={plan}
                    userId={user?.uid}
                    isSelected={selectedPlan?.id === plan.id}
                    onSelect={handlePlanSelect}
                  />
                ))}
              </div>
            </div>
          )}

          {activeTab === "offers" && (
            <div className="flex justify-center">
              <div className="w-full max-w-md">
                {offerPlans.map((plan, index) => (
                  <OfferPlan
                    key={index}
                    plan={plan}
                    userId={user?.uid}
                    isSelected={selectedPlan?.id === plan.id}
                    onSelect={handlePlanSelect}
                  />
                ))}
              </div>
            </div>
          )}

          {activeTab === "exam" && (
            <>
              <div className="flex flex-col items-center">
                <div className="w-full max-w-md rounded-3xl bg-white shadow-lg">
                  {/* Yellow Header */}
                  <div className="rounded-t-3xl bg-[#FFBF00] px-4 py-2 text-center">
                    <span className="text-sm font-semibold text-black">
                      {t("plans-modal.exam-prep.header")}
                    </span>
                  </div>
                  {/* Main Content */}
                  <div className="px-8 py-5 text-center">
                    <div className="mb-2 text-5xl font-semibold text-black">
                      $499
                    </div>
                    <div className="mb-4 text-xl font-semibold text-black">
                      {t("plans-modal.exam-prep.title")}
                    </div>
                    {/* Features List */}
                    <div className="mb-4 space-y-3 text-base font-normal text-black">
                      <div>{t("plans-modal.exam-prep.features.classes")}</div>
                      <div>
                        {t("plans-modal.exam-prep.features.unlimited-groups")}
                      </div>
                      <div>{t("plans-modal.exam-prep.features.support")}</div>
                      <div>
                        {t("plans-modal.exam-prep.features.language-partner")}
                      </div>
                      <div>
                        {t("plans-modal.exam-prep.features.guarantee")}{" "}
                        <span className="font-bold">**</span>
                      </div>
                    </div>
                    {/* Disclaimer Text */}
                    <div className="mb-4 px-2 text-sm font-normal leading-relaxed text-black">
                      {t("plans-modal.exam-prep.disclaimer")}
                    </div>
                    {/* Buttons */}
                    <div className="mb-2">
                      <button
                        className="w-full rounded-full border border-[#042F0C] bg-[#14B82C] p-3 text-base font-medium text-black transition-colors hover:bg-green-600"
                        onClick={async () => {
                          if (!user?.uid || !user?.email) {
                            toast.error(t("plans-modal.exam-prep.errors.login-required"));
                            return;
                          }
                          setIsLoading(true);
                          try {
                            const response = await fetch(
                              "https://createcheckoutsession-3idvfneyra-uc.a.run.app",
                              {
                                method: "POST",
                                headers: { "Content-Type": "application/json" },
                                body: JSON.stringify({
                                  email: user.email,
                                  studentId: user.uid,
                                  context: "web",
                                }),
                              },
                            );
                            const data = await response.json();
                            if (data.url) {
                              window.location.href = data.url;
                            } else {
                              toast.error(t("plans-modal.exam-prep.errors.checkout-failed"));
                            }
                          } catch (err) {
                            toast.error(t("plans-modal.exam-prep.errors.checkout-error"));
                          } finally {
                            setIsLoading(false);
                          }
                        }}
                        disabled={isLoading}
                      >
                        {isLoading ? t("plans-modal.exam-prep.buttons.processing") : t("plans-modal.exam-prep.buttons.buy-now")}
                      </button>
                      <button
                        className="w-full rounded-full px-6 py-3 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50"
                        onClick={() =>
                          window.open(
                            "https://calendly.com/bammbuu-languages/info-call-llamada-de-informacion",
                            "_blank",
                          )
                        }
                      >
                        {t("plans-modal.exam-prep.buttons.learn-more")}
                      </button>
                    </div>
                  </div>
                </div>
                {/* Fine Print */}
              </div>
              <div className="mt-4 w-full text-center text-sm font-normal italic leading-relaxed text-gray-500">
                <span>**</span>
                {t("plans-modal.exam-prep.guarantee-terms")}
              </div>
            </>
          )}

          {selectedPlan && (
            <div className="mt-8">
              <button
                onClick={handleSubscribe}
                disabled={
                  isLoading || (hasActiveFreeTrial && selectedPlan.price === 0)
                }
                className={`mx-auto block w-full max-w-md py-3 text-[#042F0C] transition-colors ${
                  hasActiveFreeTrial && selectedPlan.price === 0
                    ? "cursor-not-allowed bg-gray-300"
                    : "bg-[#14B82C] hover:bg-[#12a528]"
                } rounded-full border border-[#042F0C] disabled:opacity-50`}
              >
                {isLoading
                  ? t("plans-modal.buttons.loading")
                  : hasActiveFreeTrial && selectedPlan.price === 0
                    ? t("plans-modal.buttons.already-activated")
                    : selectedPlan.type === "free_trial"
                      ? t("plans-modal.buttons.subscribe")
                      : `${t("plans-modal.buttons.buy")} - $${selectedPlan.price}`}
              </button>
              {hasActiveFreeTrial && selectedPlan.price === 0 && (
                <p className="mt-2 text-center text-sm text-orange-500">
                  {t("plans-modal.messages.already-active-subscription")}
                </p>
              )}
            </div>
          )}
        </div>
      </Modal>

      {/* Free Trial Confirmation Modal */}
      <Modal
        isOpen={showFreeTrialModal}
        onRequestClose={() => {
          if (!isLoading) {
            setShowFreeTrialModal(false);
            setSelectedPlan(null); // Deselect plan when closing
            setFreeTrialActivated(false); // Reset activation state
          }
        }}
        className="fixed left-1/2 top-1/2 w-full max-w-md -translate-x-1/2 -translate-y-1/2 transform rounded-3xl bg-white p-6 outline-none"
        overlayClassName="fixed inset-0 bg-black bg-opacity-50"
        style={{
          overlay: {
            zIndex: 1100,
            backgroundColor: "rgba(0, 0, 0, 0.75)",
          },
          content: {
            zIndex: 1101,
          },
        }}
      >
        <div className="text-center">
          {!freeTrialActivated ? (
            <>
              <div className="mb-4 flex justify-center">
                <img alt={t("plans-modal.free-trial.alt.icon")} src="/svgs/account-created.svg" />
              </div>
              <h2 className="mb-4 text-2xl font-semibold">
                {t("plans-modal.free-trial.title")}
              </h2>
              <p className="mb-6 text-gray-600">
                {t("plans-modal.free-trial.description")}
              </p>
              <div className="flex space-x-4">
                <button
                  onClick={() => {
                    setShowFreeTrialModal(false);
                    setSelectedPlan(null); // Deselect plan when cancelling
                    setFreeTrialActivated(false); // Reset activation state
                  }}
                  disabled={isLoading}
                  className="w-full rounded-full bg-gray-200 py-2 text-gray-700 hover:bg-gray-300"
                >
                  {t("plans-modal.free-trial.buttons.cancel")}
                </button>
                <button
                  onClick={activateFreeTrial}
                  disabled={isLoading}
                  className="w-full rounded-full border border-[#042F0C] bg-[#14B82C] py-2 text-[#042F0C] disabled:opacity-50"
                >
                  {isLoading ? t("plans-modal.free-trial.buttons.activating") : t("plans-modal.free-trial.buttons.activate")}
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="mb-4 flex justify-center">
                <img alt={t("plans-modal.free-trial.alt.success")} src="/svgs/account-created.svg" />
              </div>
              <h2 className="mb-4 text-2xl font-semibold text-gray-900">
                {t("plans-modal.free-trial.success.title")}
              </h2>
              <p className="mb-6 text-gray-600">
                {t("plans-modal.free-trial.success.description")}
              </p>
            </>
          )}
        </div>
      </Modal>
    </>
  );
};

export default PlansModal;
